import { useState } from 'react';

import { usePermissions } from 'hooks/usePermissions';
import { MerchantShowAccountsForm } from 'pages/Merchants/MerchantsShow/MerchantShowAccounts/components/MerchantShowAccountsForm/MerchantShowAccountsForm';
import { MerchantShowAccountsFormSkeleton } from 'pages/Merchants/MerchantsShow/MerchantShowAccounts/components/MerchantShowAccountsForm/MerchantShowAccountsFormSkeleton';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Form, RecordContextProvider, useNotify, useUpdate } from 'react-admin';
import { Button, DeleteButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { useMerchantAccountCredentials } from 'shared/widgets/AccountsCredentialsDialog/hooks/useMerchantAccountCredentials';
import { MerchantAccount } from 'types/embedded/merchantAccount';
import { Permission } from 'types/embedded/permission';

type Props = {
  open: boolean;
  onClose: () => void;
  onDelete: (record: MerchantAccount) => void;
  record?: MerchantAccount;
  merchantId?: string;
};

type EditFormValues = {
  merchant: string;
  provider: string;
  gateway: string;
  name: string;
  credentials: Record<string, any>;
};

export const MerchantShowAccountsEditDialog: React.FC<Props> = ({
  open,
  onClose,
  onDelete,
  merchantId,
  record,
}) => {
  const translate = useTypedTranslate();

  const [errors, setErrors] = useState<string[]>([]);
  const { hasResourcePermissions } = usePermissions();
  const [update] = useUpdate();
  const notify = useNotify();
  const { credentials, isLoading: isCredentialsLoading } = useMerchantAccountCredentials(
    record?.originId,
    open,
  );

  const onSubmit = (formValues: EditFormValues) => {
    update(
      'merchant_accounts',
      {
        id: record?.id,
        data: {
          ...formValues,
          merchants: [merchantId],
        },
      },
      {
        onSuccess: () => {
          notify(translate('app.merchantAccounts.merchantAccountUpdatedSuccessfully'), {
            type: 'success',
          });
          onClose();
        },
        onError: (error) => {
          setErrors((error as Error)?.message.split('\n'));
          notify(`${translate('app.common.error')}: ${(error as Error).message}`, {
            type: 'error',
          });
        },
      },
    );
  };

  return (
    <Dialog maxWidth="lg" onClose={onClose} open={open}>
      <RecordContextProvider value={record && { ...record, credentials }}>
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title={translate('app.merchantAccounts.editMerchantAccount')}
          titleSize="smallBold"
        />
        <Form onSubmit={onSubmit as any}>
          <CardContent>
            {isCredentialsLoading ? (
              <MerchantShowAccountsFormSkeleton />
            ) : (
              <MerchantShowAccountsForm errors={errors} isEditMode merchantId={merchantId} />
            )}
          </CardContent>
          <CardActionsButtons
            leftActionsSlot={
              hasResourcePermissions('merchant_accounts', [Permission.Full, Permission.Delete]) && (
                <DeleteButton
                  onClick={() => {
                    onClose();
                    record && onDelete(record);
                  }}
                  variant="outlined"
                />
              )
            }
            rightActionsSlot={[
              <Button
                key="cancel"
                label={translate('app.buttons.cancel')}
                onClick={onClose}
                variant="outlined"
              />,
              <SaveButton
                disabled={isCredentialsLoading}
                key="save"
                label={translate('app.buttons.save')}
              />,
            ]}
          />
        </Form>
      </RecordContextProvider>
    </Dialog>
  );
};
